import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import {
  FileText,
  Download,
  ExternalLink,
  Upload,
  Plus,
  Search,
  Filter,
  BookOpen,
  ScrollText,
  FileCheck,
  GraduationCap,
  File,
  Lock,
  Crown,
  Star,
  Trash2,
  Edit,
  Loader2
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// Category configuration
const CATEGORIES = {
  bylaws: { label: 'Bylaws & Constitution', icon: ScrollText, color: 'bg-blue-100 text-blue-700' },
  rules: { label: 'Rules & Regulations', icon: BookOpen, color: 'bg-purple-100 text-purple-700' },
  forms: { label: 'Forms & Applications', icon: FileCheck, color: 'bg-green-100 text-green-700' },
  guides: { label: 'Guides & Handbooks', icon: GraduationCap, color: 'bg-amber-100 text-amber-700' },
  templates: { label: 'Templates', icon: File, color: 'bg-pink-100 text-pink-700' },
  educational: { label: 'Educational Materials', icon: GraduationCap, color: 'bg-teal-100 text-teal-700' },
  other: { label: 'Other Resources', icon: FileText, color: 'bg-gray-100 text-gray-700' }
};

/**
 * Club Resources Page - Members-only resource library
 */
const ClubResources = () => {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [resources, setResources] = useState([]);
  const [categories, setCategories] = useState([]);
  const [membership, setMembership] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [uploading, setUploading] = useState(false);

  // Form state for new resource
  const [newResource, setNewResource] = useState({
    title: '',
    description: '',
    category: 'other',
    external_url: '',
    is_featured: false
  });
  const [selectedFile, setSelectedFile] = useState(null);

  useEffect(() => {
    if (user) {
      fetchMembership();
      fetchResources();
      fetchCategories();
    }
  }, [user, token]);

  const fetchMembership = async () => {
    try {
      const response = await fetch(`${API}/api/clubs/my-membership`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setMembership(data);
      }
    } catch (error) {
      console.error('Error fetching membership:', error);
    }
  };

  const fetchResources = async () => {
    try {
      const url = selectedCategory === 'all' 
        ? `${API}/api/clubs/resources/all`
        : `${API}/api/clubs/resources/all?category=${selectedCategory}`;
      
      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setResources(data);
      } else if (response.status === 403) {
        // User is not a club member
        setResources([]);
      }
    } catch (error) {
      console.error('Error fetching resources:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await fetch(`${API}/api/clubs/resources/categories`);
      if (response.ok) {
        const data = await response.json();
        setCategories(data);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  useEffect(() => {
    if (user && membership?.can_access_resources) {
      fetchResources();
    }
  }, [selectedCategory]);

  const handleAddResource = async (e) => {
    e.preventDefault();
    setUploading(true);

    try {
      const formData = new FormData();
      formData.append('title', newResource.title);
      formData.append('description', newResource.description || '');
      formData.append('category', newResource.category);
      formData.append('external_url', newResource.external_url || '');
      formData.append('is_featured', newResource.is_featured);
      formData.append('sort_order', 0);
      
      if (selectedFile) {
        formData.append('file', selectedFile);
      }

      const response = await fetch(`${API}/api/clubs/resources`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });

      if (response.ok) {
        toast.success('Resource added successfully');
        setShowAddDialog(false);
        setNewResource({
          title: '',
          description: '',
          category: 'other',
          external_url: '',
          is_featured: false
        });
        setSelectedFile(null);
        fetchResources();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to add resource');
      }
    } catch (error) {
      toast.error('Error adding resource');
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteResource = async (resourceId) => {
    if (!confirm('Are you sure you want to delete this resource?')) return;

    try {
      const response = await fetch(`${API}/api/clubs/resources/${resourceId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Resource deleted');
        fetchResources();
      } else {
        toast.error('Failed to delete resource');
      }
    } catch (error) {
      toast.error('Error deleting resource');
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Filter resources by search query
  const filteredResources = resources.filter(res => 
    res.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (res.description && res.description.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  // Show access denied for non-members
  if (!user) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="max-w-md w-full text-center">
          <CardContent className="pt-8 pb-6">
            <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
              <Lock className="w-8 h-8 text-muted-foreground" />
            </div>
            <h2 className="text-xl font-heading font-medium mb-2">Login Required</h2>
            <p className="text-muted-foreground mb-6">
              Please log in to access club resources.
            </p>
            <Button onClick={() => navigate('/login')}>
              Log In
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  // Show upgrade prompt for non-club members
  if (membership && !membership.can_access_resources) {
    return (
      <div className="min-h-screen bg-background" data-testid="club-resources-page">
        {/* Hero Section */}
        <div className="bg-gradient-to-r from-[#1e3a5f] to-[#2d4a6f] text-white py-16">
          <div className="container mx-auto px-4 text-center">
            <div className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center mx-auto mb-6">
              <BookOpen className="w-8 h-8" />
            </div>
            <h1 className="text-4xl font-heading font-medium mb-4">
              Club Resources
            </h1>
            <p className="text-white/80 text-lg max-w-2xl mx-auto">
              Exclusive resources available to club members including bylaws, guides, forms, and educational materials.
            </p>
          </div>
        </div>

        {/* Access Denied Card */}
        <div className="container mx-auto px-4 py-12">
          <Card className="max-w-2xl mx-auto text-center border-amber-200 bg-amber-50">
            <CardContent className="pt-8 pb-6">
              <div className="w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-4">
                <Crown className="w-8 h-8 text-amber-600" />
              </div>
              <h2 className="text-xl font-heading font-medium mb-2">Club Membership Required</h2>
              <p className="text-muted-foreground mb-6">
                {membership?.upgrade_message || 'Upgrade to PAID membership to join a club and access exclusive resources.'}
              </p>
              <p className="text-sm text-muted-foreground mb-6">
                As a FREE member, you have access to the platform but are not yet a member of any club. 
                Club membership gives you access to:
              </p>
              <div className="grid grid-cols-2 gap-4 text-left max-w-md mx-auto mb-8">
                <div className="flex items-start gap-2">
                  <FileText className="w-4 h-4 text-primary mt-1" />
                  <span className="text-sm">Club bylaws & documents</span>
                </div>
                <div className="flex items-start gap-2">
                  <BookOpen className="w-4 h-4 text-primary mt-1" />
                  <span className="text-sm">Educational materials</span>
                </div>
                <div className="flex items-start gap-2">
                  <FileCheck className="w-4 h-4 text-primary mt-1" />
                  <span className="text-sm">Forms & applications</span>
                </div>
                <div className="flex items-start gap-2">
                  <GraduationCap className="w-4 h-4 text-primary mt-1" />
                  <span className="text-sm">Training guides</span>
                </div>
              </div>
              <div className="flex justify-center gap-4 flex-wrap">
                <Button onClick={() => navigate('/membership')} variant="outline">
                  View Membership Options
                </Button>
                <Button onClick={() => navigate('/settings')} className="btn-primary">
                  <Star className="w-4 h-4 mr-2" />
                  Upgrade to PAID
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="club-resources-page">
      {/* Hero Section with Club Branding */}
      <div className="bg-gradient-to-r from-[#1e3a5f] to-[#2d4a6f] text-white py-12">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                  <BookOpen className="w-5 h-5" />
                </div>
                <Badge variant="secondary" className="bg-[#dc2626] text-white border-0">
                  All-American Dog Club
                </Badge>
              </div>
              <h1 className="text-3xl font-heading font-medium mb-2">
                Club Resources
              </h1>
              <p className="text-white/80">
                "Every Dog Is a Champion" - Access exclusive member resources
              </p>
            </div>
            
            {user?.is_admin && (
              <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
                <DialogTrigger asChild>
                  <Button className="bg-white text-[#1e3a5f] hover:bg-white/90">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Resource
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-lg">
                  <DialogHeader>
                    <DialogTitle>Add New Resource</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleAddResource} className="space-y-4">
                    <div>
                      <label className="text-sm font-medium">Title *</label>
                      <Input
                        value={newResource.title}
                        onChange={(e) => setNewResource({...newResource, title: e.target.value})}
                        placeholder="Resource title"
                        required
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium">Description</label>
                      <Textarea
                        value={newResource.description}
                        onChange={(e) => setNewResource({...newResource, description: e.target.value})}
                        placeholder="Brief description"
                        rows={3}
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium">Category</label>
                      <Select 
                        value={newResource.category} 
                        onValueChange={(v) => setNewResource({...newResource, category: v})}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {Object.entries(CATEGORIES).map(([key, cat]) => (
                            <SelectItem key={key} value={key}>{cat.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="text-sm font-medium">External URL (optional)</label>
                      <Input
                        value={newResource.external_url}
                        onChange={(e) => setNewResource({...newResource, external_url: e.target.value})}
                        placeholder="https://..."
                        type="url"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium">Or Upload File</label>
                      <Input
                        type="file"
                        accept=".pdf,.doc,.docx,.txt,.xls,.xlsx,.ppt,.pptx"
                        onChange={(e) => setSelectedFile(e.target.files[0])}
                        className="mt-1"
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        PDF, DOC, DOCX, TXT, XLS, XLSX, PPT, PPTX (max 10MB)
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        id="is_featured"
                        checked={newResource.is_featured}
                        onChange={(e) => setNewResource({...newResource, is_featured: e.target.checked})}
                      />
                      <label htmlFor="is_featured" className="text-sm">Featured resource</label>
                    </div>
                    <div className="flex justify-end gap-2">
                      <Button type="button" variant="outline" onClick={() => setShowAddDialog(false)}>
                        Cancel
                      </Button>
                      <Button type="submit" disabled={uploading}>
                        {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
                        Add Resource
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            )}
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        {/* Filters */}
        <div className="flex flex-wrap gap-4 mb-8">
          <div className="flex-1 min-w-[200px] max-w-md">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search resources..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>
          <Select value={selectedCategory} onValueChange={setSelectedCategory}>
            <SelectTrigger className="w-[200px]">
              <Filter className="w-4 h-4 mr-2" />
              <SelectValue placeholder="All Categories" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {Object.entries(CATEGORIES).map(([key, cat]) => (
                <SelectItem key={key} value={key}>{cat.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Featured Resources */}
        {filteredResources.filter(r => r.is_featured).length > 0 && (
          <div className="mb-8">
            <h2 className="text-lg font-heading font-medium mb-4 flex items-center gap-2">
              <Star className="w-5 h-5 text-amber-500" />
              Featured Resources
            </h2>
            <div className="grid md:grid-cols-2 gap-4">
              {filteredResources.filter(r => r.is_featured).map((resource) => {
                const CategoryIcon = CATEGORIES[resource.category]?.icon || FileText;
                const categoryStyle = CATEGORIES[resource.category]?.color || 'bg-gray-100 text-gray-700';
                
                return (
                  <Card key={resource.id} className="border-amber-200 bg-amber-50/30">
                    <CardContent className="p-4">
                      <div className="flex items-start gap-3">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${categoryStyle}`}>
                          <CategoryIcon className="w-5 h-5" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="font-medium truncate">{resource.title}</h3>
                            <Badge variant="outline" className="text-amber-600 border-amber-300">
                              Featured
                            </Badge>
                          </div>
                          {resource.description && (
                            <p className="text-sm text-muted-foreground line-clamp-2 mb-2">
                              {resource.description}
                            </p>
                          )}
                          <div className="flex items-center gap-2">
                            {resource.external_url ? (
                              <a
                                href={resource.external_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
                              >
                                <ExternalLink className="w-3 h-3" />
                                Visit Resource
                              </a>
                            ) : resource.file_url ? (
                              <a
                                href={`${API}${resource.file_url}`}
                                download={resource.file_name}
                                className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
                              >
                                <Download className="w-3 h-3" />
                                Download ({formatFileSize(resource.file_size)})
                              </a>
                            ) : null}
                            {user?.is_admin && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteResource(resource.id)}
                                className="ml-auto text-red-600 hover:text-red-700 hover:bg-red-50"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        )}

        {/* All Resources by Category */}
        {Object.entries(CATEGORIES).map(([categoryKey, categoryInfo]) => {
          const categoryResources = filteredResources.filter(r => r.category === categoryKey && !r.is_featured);
          if (categoryResources.length === 0) return null;
          
          const CategoryIcon = categoryInfo.icon;
          
          return (
            <div key={categoryKey} className="mb-8">
              <h2 className="text-lg font-heading font-medium mb-4 flex items-center gap-2">
                <CategoryIcon className="w-5 h-5" />
                {categoryInfo.label}
              </h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {categoryResources.map((resource) => (
                  <Card key={resource.id} className="hover:shadow-md transition-shadow">
                    <CardContent className="p-4">
                      <div className="flex items-start gap-3">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${categoryInfo.color}`}>
                          <CategoryIcon className="w-5 h-5" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-medium truncate mb-1">{resource.title}</h3>
                          {resource.description && (
                            <p className="text-sm text-muted-foreground line-clamp-2 mb-2">
                              {resource.description}
                            </p>
                          )}
                          <div className="flex items-center gap-2">
                            {resource.external_url ? (
                              <a
                                href={resource.external_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
                              >
                                <ExternalLink className="w-3 h-3" />
                                Visit
                              </a>
                            ) : resource.file_url ? (
                              <a
                                href={`${API}${resource.file_url}`}
                                download={resource.file_name}
                                className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
                              >
                                <Download className="w-3 h-3" />
                                Download
                              </a>
                            ) : null}
                            {user?.is_admin && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteResource(resource.id)}
                                className="ml-auto text-red-600 hover:text-red-700 hover:bg-red-50 h-8 w-8 p-0"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          );
        })}

        {/* Empty State */}
        {filteredResources.length === 0 && (
          <Card className="text-center py-12">
            <CardContent>
              <FileText className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-medium mb-2">No Resources Found</h3>
              <p className="text-muted-foreground">
                {searchQuery 
                  ? 'No resources match your search. Try different keywords.'
                  : 'No resources available in this category yet.'}
              </p>
            </CardContent>
          </Card>
        )}

        {/* Info Card */}
        <Card className="mt-8 bg-slate-50 border-slate-200">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center flex-shrink-0">
                <BookOpen className="w-5 h-5 text-slate-600" />
              </div>
              <div>
                <h3 className="font-medium mb-2">About Club Resources</h3>
                <p className="text-sm text-muted-foreground mb-3">
                  These resources are provided exclusively for All-American Dog Club members. 
                  Many documents come from the American Kennel Club (AKC) and other authoritative sources 
                  to help club members understand governance, regulations, and best practices.
                </p>
                <p className="text-sm text-muted-foreground">
                  <strong>Note:</strong> External links will take you to third-party websites. 
                  Please review each site's terms and privacy policies.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ClubResources;
